import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const emptyItem = { description: '', quantity: 1, unit_price: '' };

export default function InvoiceBuilder() {
     const { inquiryId, id: invoiceId } = useParams();
     const navigate = useNavigate();
     const [inquiry, setInquiry] = useState(null);
     const [form, setForm] = useState({
          customer_name: '',
          customer_email: '',
          line_items: [{ ...emptyItem }],
          tax_percent: 0,
          due_date: '',
          notes: '',
     });
     const [status, setStatus] = useState('draft');
     const [stripeLink, setStripeLink] = useState(null);
     const [saving, setSaving] = useState(false);
     const [sending, setSending] = useState(false);
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          loadData();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     async function loadData() {
          if (invoiceId) {
               const { data } = await supabase
                    .from('invoices')
                    .select('*')
                    .eq('id', invoiceId)
                    .single();
               if (data) {
                    const items = (data.line_items || []).map((li) => ({
                         description: li.description,
                         quantity: li.quantity,
                         unit_price: (li.unit_price_cents / 100).toFixed(2),
                    }));
                    setForm({
                         customer_name: data.customer_name,
                         customer_email: data.customer_email,
                         line_items: items.length > 0 ? items : [{ ...emptyItem }],
                         tax_percent: data.subtotal_cents > 0 ? ((data.tax_cents / data.subtotal_cents) * 100) : 0,
                         due_date: data.due_date || '',
                         notes: data.notes || '',
                    });
                    setStatus(data.status);
                    setStripeLink(data.stripe_payment_link);
                    if (data.inquiry_id) {
                         const { data: inq } = await supabase
                              .from('inquiries')
                              .select('*')
                              .eq('id', data.inquiry_id)
                              .single();
                         setInquiry(inq);
                    }
               }
          } else if (inquiryId) {
               const { data: inq } = await supabase
                    .from('inquiries')
                    .select('*')
                    .eq('id', inquiryId)
                    .single();
               if (inq) {
                    setInquiry(inq);
                    setForm((f) => ({ ...f, customer_name: inq.name, customer_email: inq.email }));
               }

               // Auto-fill from accepted proposal if one exists
               const { data: proposals } = await supabase
                    .from('proposals')
                    .select('*')
                    .eq('inquiry_id', inquiryId)
                    .eq('status', 'accepted')
                    .order('created_at', { ascending: false })
                    .limit(1);

               if (proposals && proposals.length > 0) {
                    const p = proposals[0];
                    const items = (p.line_items || []).map((li) => ({
                         description: li.description,
                         quantity: li.quantity,
                         unit_price: (li.unit_price_cents / 100).toFixed(2),
                    }));
                    setForm((f) => ({
                         ...f,
                         line_items: items.length > 0 ? items : [{ ...emptyItem }],
                         tax_percent: p.subtotal_cents > 0 ? ((p.tax_cents / p.subtotal_cents) * 100) : 0,
                         notes: p.notes || '',
                    }));
               }
          }
          setLoading(false);
     }

     function calcSubtotal() {
          return form.line_items.reduce((sum, li) => {
               return sum + Math.round((parseFloat(li.unit_price) || 0) * 100) * (parseInt(li.quantity) || 0);
          }, 0);
     }

     function calcTax() {
          return Math.round(calcSubtotal() * (form.tax_percent / 100));
     }

     function updateItem(idx, field, value) {
          const items = [...form.line_items];
          items[idx] = { ...items[idx], [field]: value };
          setForm({ ...form, line_items: items });
     }

     function addItem() {
          setForm({ ...form, line_items: [...form.line_items, { ...emptyItem }] });
     }

     function removeItem(idx) {
          if (form.line_items.length <= 1) return;
          setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
     }

     async function generateNumber() {
          const today = format(new Date(), 'yyyyMMdd');
          const { count } = await supabase
               .from('invoices')
               .select('*', { count: 'exact', head: true })
               .like('invoice_number', `INV-${today}%`);
          return `INV-${today}-${String((count || 0) + 1).padStart(3, '0')}`;
     }

     async function save() {
          setSaving(true);
          const subtotal = calcSubtotal();
          const tax = calcTax();
          const lineItems = form.line_items.map((li) => ({
               description: li.description,
               quantity: parseInt(li.quantity) || 1,
               unit_price_cents: Math.round((parseFloat(li.unit_price) || 0) * 100),
               total_cents: Math.round((parseFloat(li.unit_price) || 0) * 100) * (parseInt(li.quantity) || 1),
          }));

          const payload = {
               inquiry_id: inquiry?.id || null,
               customer_name: form.customer_name,
               customer_email: form.customer_email,
               line_items: lineItems,
               subtotal_cents: subtotal,
               tax_cents: tax,
               total_cents: subtotal + tax,
               due_date: form.due_date || null,
               notes: form.notes || null,
               updated_at: new Date().toISOString(),
          };

          if (invoiceId) {
               await supabase.from('invoices').update(payload).eq('id', invoiceId);
          } else {
               payload.invoice_number = await generateNumber();
               payload.status = 'draft';
               const { data } = await supabase.from('invoices').insert(payload).select().single();
               if (data) navigate(`/admin/invoices/${data.id}/edit`, { replace: true });
          }
          setSaving(false);
     }

     async function sendInvoice() {
          await save();
          setSending(true);
          const iid = invoiceId;
          if (!iid) {
               setSending(false);
               return;
          }
          const { data, error } = await supabase.functions.invoke('create-invoice-link', {
               body: { invoiceId: iid },
          });
          if (error) {
               alert('Error creating invoice: ' + error.message);
          } else {
               setStatus('sent');
               setStripeLink(data?.paymentLink);
               alert('Invoice sent!');
          }
          setSending(false);
     }

     const inputClasses =
          'bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) return <p className='text-darker/60'>Loading...</p>;

     const subtotal = calcSubtotal();
     const tax = calcTax();
     const total = subtotal + tax;

     return (
          <div>
               <div className='flex items-center gap-4 mb-6'>
                    <button
                         onClick={() => inquiry ? navigate(`/admin/inquiries/${inquiry.id}`) : navigate('/admin/inquiries')}
                         className='text-darker/40 hover:text-darker duration-300'
                    >
                         &larr; Back
                    </button>
                    <h1 className='text-darker text-2xl font-bold'>
                         {invoiceId ? 'Edit Invoice' : 'New Invoice'}
                    </h1>
               </div>

               <div className='space-y-6'>
                    {/* Customer Info */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Customer</h2>
                         <div className='grid tablet:grid-cols-2 gap-4'>
                              <input
                                   type='text'
                                   placeholder='Customer Name'
                                   value={form.customer_name}
                                   onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                   className={inputClasses + ' w-full'}
                              />
                              <input
                                   type='email'
                                   placeholder='Customer Email'
                                   value={form.customer_email}
                                   onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                                   className={inputClasses + ' w-full'}
                              />
                         </div>
                    </div>

                    {/* Line Items */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Line Items</h2>
                         <div className='space-y-3'>
                              {form.line_items.map((item, idx) => (
                                   <div key={idx} className='grid grid-cols-12 gap-3 items-center'>
                                        <input
                                             type='text'
                                             placeholder='Description'
                                             value={item.description}
                                             onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                             className={inputClasses + ' col-span-6'}
                                        />
                                        <input
                                             type='number'
                                             placeholder='Qty'
                                             min='1'
                                             value={item.quantity}
                                             onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                             className={inputClasses + ' col-span-2'}
                                        />
                                        <input
                                             type='number'
                                             placeholder='Price'
                                             step='0.01'
                                             min='0'
                                             value={item.unit_price}
                                             onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                                             className={inputClasses + ' col-span-3'}
                                        />
                                        <button
                                             onClick={() => removeItem(idx)}
                                             className='col-span-1 text-red-400 hover:text-red-700 text-center'
                                        >
                                             &times;
                                        </button>
                                   </div>
                              ))}
                         </div>
                         <button onClick={addItem} className='mt-3 text-yellow text-sm hover:underline'>
                              + Add Line Item
                         </button>

                         <div className='mt-6 border-t border-gray-200 pt-4 space-y-2 text-sm'>
                              <div className='flex justify-between text-darker/60'>
                                   <span>Subtotal</span>
                                   <span>${(subtotal / 100).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between items-center text-darker/60'>
                                   <div className='flex items-center gap-2'>
                                        <span>Tax</span>
                                        <input
                                             type='number'
                                             step='0.1'
                                             min='0'
                                             value={form.tax_percent}
                                             onChange={(e) => setForm({ ...form, tax_percent: parseFloat(e.target.value) || 0 })}
                                             className={inputClasses + ' w-20 text-center text-sm py-1'}
                                        />
                                        <span>%</span>
                                   </div>
                                   <span>${(tax / 100).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between text-darker font-bold text-lg pt-2 border-t border-gray-200'>
                                   <span>Total</span>
                                   <span className='text-yellow'>${(total / 100).toLocaleString()}</span>
                              </div>
                         </div>
                    </div>

                    {/* Additional Info */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Details</h2>
                         <div className='grid tablet:grid-cols-2 gap-4'>
                              <div>
                                   <label className='text-darker/40 text-sm mb-1 block'>Due Date</label>
                                   <input
                                        type='date'
                                        value={form.due_date}
                                        onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                                        className={inputClasses + ' w-full'}
                                   />
                              </div>
                         </div>
                         <div className='mt-4'>
                              <label className='text-darker/40 text-sm mb-1 block'>Notes</label>
                              <textarea
                                   value={form.notes}
                                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                   className={inputClasses + ' w-full h-24 resize-none'}
                                   placeholder='Payment notes, special instructions, etc.'
                              />
                         </div>
                    </div>

                    {/* Stripe Link Info */}
                    {stripeLink && (
                         <div className='bg-white rounded-xl border border-gray-200 p-6'>
                              <h2 className='text-darker font-bold mb-2'>Payment Link</h2>
                              <p className='text-darker/60 text-sm break-all'>{stripeLink}</p>
                         </div>
                    )}

                    {/* Actions */}
                    <div className='flex gap-3'>
                         <button
                              onClick={save}
                              disabled={saving}
                              className='px-6 py-3 bg-gray-50 text-darker rounded-lg font-bold hover:bg-gray-100 duration-300 disabled:opacity-50'
                         >
                              {saving ? 'Saving...' : 'Save Draft'}
                         </button>
                         {invoiceId && (status === 'draft' || status === 'sent') && (
                              <button
                                   onClick={sendInvoice}
                                   disabled={sending}
                                   className='px-6 py-3 bg-yellow text-black rounded-lg font-bold hover:bg-yellow/90 duration-300 disabled:opacity-50'
                              >
                                   {sending ? 'Creating Payment Link...' : status === 'sent' ? 'Resend Invoice' : 'Create Payment Link & Send'}
                              </button>
                         )}
                         {status === 'paid' && (
                              <span className='px-6 py-3 bg-green-500/20 text-green-300 rounded-lg text-sm flex items-center'>
                                   Paid
                              </span>
                         )}
                    </div>
               </div>
          </div>
     );
}
